import { useState, useRef, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { 
    AlertTriangle,
    AlertCircle,
    CheckCircle2,
    User,
    Trash2,
    Loader2
} from 'lucide-react';
import { useEnhancedToast } from "@/components/ui/enhanced-toast";
import { deleteRootUser } from "@/api/root-admin/root-admin.api";
import { cn } from "@/lib/utils";

interface TeamMember {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
    role: string;
}

interface DeleteUserDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    user: TeamMember | null;
    onSuccess?: () => void;
}

export function DeleteUserDialog({
    open,
    onOpenChange,
    user,
    onSuccess,
}: DeleteUserDialogProps) {
    const { error, success } = useEnhancedToast();
    const [isDeleting, setIsDeleting] = useState(false);

    const errorRef = useRef(error);
    const successRef = useRef(success);
    useEffect(() => {
        errorRef.current = error;
        successRef.current = success;
    }, [error, success]);

    const handleDelete = async () => {
        if (!user) return;

        setIsDeleting(true);
        try {
            const result = await deleteRootUser(user.id);
            
            if (result.error) {
                throw new Error(result.error);
            }

            successRef.current({
                icon: CheckCircle2,
                title: "User Deleted",
                description: `${user.email} has been removed from the platform team`,
                duration: 4000,
            });

            onOpenChange(false);
            onSuccess?.();
        } catch (err) {
            errorRef.current({
                icon: AlertCircle,
                title: "Delete Failed",
                description: err instanceof Error ? err.message : "Could not delete user",
                duration: 4000,
            });
        } finally {
            setIsDeleting(false);
        }
    };

    if (!user) return null;

    const hasSignedUp = user.first_name && user.last_name;
    const initials = hasSignedUp 
        ? `${user.first_name[0]}${user.last_name[0]}`.toUpperCase()
        : null;
    const displayName = hasSignedUp 
        ? `${user.first_name} ${user.last_name}` 
        : user.email;

    return (
        <AlertDialog open={open} onOpenChange={onOpenChange}>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <div className="flex items-center gap-3 mb-2">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-destructive/10">
                            <AlertTriangle className="h-5 w-5 text-destructive" />
                        </div>
                        <AlertDialogTitle>Delete Team Member</AlertDialogTitle>
                    </div>
                    <AlertDialogDescription asChild>
                        <div className="space-y-4">
                            <p>
                                Are you sure you want to delete this user? This action will:
                            </p>
                            <ul className="list-disc list-inside text-sm space-y-1 text-muted-foreground">
                                <li>Remove the user from the platform team</li>
                                <li>Revoke all organization access grants</li>
                                <li>Cancel any pending invitations</li>
                            </ul>
                            
                            {/* User Card */}
                            <div className="rounded-lg border bg-muted/50 p-4 mt-4">
                                <div className="flex items-center gap-3">
                                    <Avatar className="h-10 w-10">
                                        <AvatarFallback className={cn(
                                            "text-sm font-semibold",
                                            hasSignedUp 
                                                ? "bg-primary/10 text-primary" 
                                                : "bg-muted text-muted-foreground"
                                        )}>
                                            {hasSignedUp ? initials : <User className="h-4 w-4" />}
                                        </AvatarFallback>
                                    </Avatar>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-medium truncate">
                                            {displayName}
                                        </p>
                                        {hasSignedUp && (
                                            <p className="text-sm text-muted-foreground truncate">
                                                {user.email}
                                            </p>
                                        )}
                                    </div>
                                    <Badge variant="secondary" className="capitalize">
                                        {user.role}
                                    </Badge>
                                </div>
                            </div>

                            <p className="text-sm font-medium text-destructive">
                                This action cannot be undone.
                            </p>
                        </div>
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
                    <Button
                        variant="destructive"
                        onClick={handleDelete}
                        disabled={isDeleting}
                        className="gap-2"
                    >
                        {isDeleting ? (
                            <>
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Deleting...
                            </>
                        ) : (
                            <>
                                <Trash2 className="h-4 w-4" />
                                Delete User
                            </>
                        )}
                    </Button>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
}
